import axios from "axios";
import userServiceApi from "./userServiceApi";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.data?.message ||
  error?.data?.error ||
  error?.message ||
  error?.error ||
  (typeof error?.response?.data === "string" ? error.response.data : null) ||
  (typeof error?.data === "string" ? error.data : null) ||
  fallback;

/**
 * Axios response interceptor in http.js returns the error object instead of
 * rejecting. Detect failed responses so OTP failures never look like success.
 */
const assertApiSuccess = (response, fallbackMessage) => {
  const status = response?.status ?? response?.response?.status;
  const isHttpError =
    response?.isAxiosError ||
    (typeof status === "number" && status >= 400) ||
    Boolean(response?.response);

  if (isHttpError) {
    const err = new Error(getErrorMessage(response, fallbackMessage));
    err.cause = response;
    throw err;
  }

  return response?.data ?? response;
};

const toServiceError = (error, fallbackMessage, code) => {
  const err = new Error(getErrorMessage(error, fallbackMessage));
  err.code = code;
  err.cause = error;
  return err;
};

/**
 * Secure deletion OTP verification service.
 *
 * APIs:
 * - GET  /api/confirm-entity-deletion?entity={ENTITY}
 * - POST /api/verify-otp?otp={otp}
 */
const deletionVerificationService = {
  /**
   * Request OTP before entity deletion.
   * @param {{ entity: string, entityId?: string|number, displayName?: string }} payload
   */
  async sendDeleteOtp(payload) {
    const entity = payload?.entity;
    if (!entity) {
      throw new Error("Unable to send verification code. Please try again.");
    }

    try {
      const response = await axios.get(
        userServiceApi.confirmEntityDeletion(entity)
      );
      return assertApiSuccess(
        response,
        "Unable to send verification code. Please try again."
      );
    } catch (error) {
      if (error?.code === "SEND_OTP_FAILED") throw error;
      throw toServiceError(
        error,
        "Unable to send verification code. Please try again.",
        "SEND_OTP_FAILED"
      );
    }
  },

  /**
   * Verify OTP for secure deletion.
   * Partner is deleted only when this resolves with verified: true.
   * @param {{ otp: string, entity?: string, entityId?: string|number }} payload
   */
  async verifyDeleteOtp(payload) {
    const otp = String(payload?.otp || "").trim();
    if (!otp) {
      const error = new Error("Invalid OTP. Please check the code and try again.");
      error.code = "INVALID_OTP";
      throw error;
    }

    try {
      const response = await axios.post(userServiceApi.verifyDeleteOtp(otp));
      const data = assertApiSuccess(
        response,
        "Invalid OTP. Please check the code and try again."
      );

      // API returns boolean: true = valid OTP, false = wrong OTP
      const isVerified =
        data === true ||
        data === "true" ||
        data?.verified === true ||
        data?.success === true ||
        data?.data === true;

      if (!isVerified) {
        const err = new Error(
          data?.message || "Invalid OTP. Please check the code and try again."
        );
        err.code = "INVALID_OTP";
        throw err;
      }

      // Backend error payload must never count as verified
      if (data?.errorLog || data?.error) {
        throw toServiceError(
          data,
          data?.message || "Invalid OTP. Please check the code and try again.",
          "INVALID_OTP"
        );
      }

      return {
        success: true,
        verified: true,
        ...(data && typeof data === "object" ? data : { data }),
      };
    } catch (error) {
      if (error?.code === "INVALID_OTP" || error?.code === "OTP_EXPIRED") {
        throw error;
      }

      const message = getErrorMessage(
        error,
        "Invalid OTP. Please check the code and try again."
      );
      const err = toServiceError(error, message, "INVALID_OTP");
      if (String(message).toLowerCase().includes("expir")) {
        err.code = "OTP_EXPIRED";
      }
      throw err;
    }
  },
};

export default deletionVerificationService;
